import React, { useState } from "react";
import { Link } from "react-router";
import { Plus, Search, Filter } from "lucide-react";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { useVariables } from "../../hooks/useVariables";
import { VariableFilters } from "../../types/variable";
import { Input } from "../../components/ui/Input";

const VariablesPage: React.FC = () => {
  const [filters, setFilters] = useState<VariableFilters>({
    page: 1,
    page_size: 10,
  });
  const [showFilters, setShowFilters] = useState(false);

  const { data: response, isLoading, error, refetch } = useVariables(filters);
  const variables = response?.variables || [];
  const totalCount = response?.total || 0;
  const totalPages =
    response?.total_pages || Math.ceil(totalCount / (filters.page_size || 10));

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search: search || undefined, page: 1 }));
  };

  const handleFilterChange = (newFilters: Partial<VariableFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page: number) =>
    setFilters((prev) => ({ ...prev, page }));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message="Failed to load variables. Please try again."
        onRetry={() => refetch()}
        className="py-12"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Variables
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage template variables for dynamic content
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link
            to="/variables/new"
            className="btn-primary inline-flex items-center px-4 py-2 font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Variable
          </Link>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search variables..."
              className="pl-10"
              value={filters.search || ""}
              onChange={(e) => handleSearch(e.target.value)}
              variant="shadow-sm"
              showLabel={false}
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 font-medium rounded-lg transition-colors bg-gray-200 text-gray-900 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 inline-flex items-center ${showFilters ? "bg-blue-50 text-blue-700" : ""}`}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="DB Name"
                type="text"
                value={filters.db_name || ""}
                onChange={(e) =>
                  handleFilterChange({ db_name: e.target.value || undefined })
                }
                variant="shadow-sm"
              />
              <Input
                label="Transform"
                type="text"
                value={filters.transform || ""}
                onChange={(e) =>
                  handleFilterChange({
                    transform: e.target.value || undefined,
                  })
                }
                variant="shadow-sm"
              />
            </div>
          </div>
        )}
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {variables.map((v) => (
            <li key={v.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
              <Link to={`/variables/${v.id}`} className="block">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-indigo-600 truncate">
                    {v.var_name}
                  </p>
                  <div className="ml-2 flex-shrink-0 flex">
                    <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {v.db_name ||
                        (v.http_request_endpoint_id ? "HTTP" : "N/A")}
                    </p>
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between">
                  <div className="sm:flex">
                    <p className="flex items-center text-sm text-gray-500 truncate max-w-2xl">
                      {v.description || "—"}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                    <p className="truncate">{v.transform || "no transform"}</p>
                  </div>
                </div>
              </Link>
            </li>
          ))}
          {variables.length === 0 && (
            <li className="px-4 py-6 text-center text-gray-500">
              No variables found
            </li>
          )}
        </ul>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">Total: {totalCount}</div>
          <div className="space-x-2">
            <button
              className="btn-secondary px-3 py-1 rounded-md"
              disabled={(filters.page || 1) <= 1}
              onClick={() => handlePageChange((filters.page || 1) - 1)}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {filters.page || 1} of {totalPages}
            </span>
            <button
              className="btn-secondary px-3 py-1 rounded-md"
              disabled={(filters.page || 1) >= totalPages}
              onClick={() => handlePageChange((filters.page || 1) + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariablesPage;
